import { Role } from "./role.model";

/**
 * The eight Keycloak groups a user can belong to (see GROUP_DEFAULT_ROUTE in
 * group-landing.util.ts), used here to bucket the permission list so admins pick
 * audience permissions the same way they are laid out on the user-creation screen.
 */
export const PERMISSION_GROUP_NAMES = [
  "ADMINISTRATION",
  "DELIVERY",
  "MESSAGING",
  "ORDER",
  "PAYMENT",
  "SOLUTION",
  "SUPPORT",
  "VOUCHER",
] as const;

export type PermissionGroupName = (typeof PERMISSION_GROUP_NAMES)[number];

/** Keycloak publishes group membership as a path (`/SOLUTION`); audienceGroups is matched against it. */
export function groupPath(group: PermissionGroupName): string {
  return `/${group}`;
}

/**
 * Authorities the backend grants but that the Role enum does not carry, because no route in
 * this UI is gated on them. Spelled out here so an announcement can still target them.
 */
const COMPANY_ALL_NUMBERS = "COMPANY_ALL_NUMBERS";
const MANAGE_PAYMENT_CONFIG = "MANAGE_PAYMENT_CONFIG";

/**
 * The permissions each group actually grants on the user-creation screen, in the order they
 * are shown there. This is a curated list, not every Role in the enum: a permission no group
 * hands out cannot be held by anyone, so offering it as an audience would only ever produce an
 * announcement nobody sees. A permission granted by two groups (MANAGE_ATTRIBUTE) is listed
 * under both — the picker writes a flat permission list, so ticking it in one group ticks it
 * in the other as well.
 *
 * The read-only roles are listed alongside their manage counterparts because they can be
 * assigned directly, without the broad group, and an announcement still has to reach them.
 *
 * SOLUTION is deliberately empty: Dashy staff get their access from the group itself, so the
 * group is targeted as a whole (audienceGroups) rather than through a permission.
 */
const PERMISSIONS_BY_GROUP: Record<PermissionGroupName, string[]> = {
  ADMINISTRATION: [
    Role.COMPANY_MANAGE,
    Role.AUDIT_MANAGE,
    Role.MANAGE_TICKET_ASSIGNMENT,
    COMPANY_ALL_NUMBERS,
    Role.MANAGE_ATTRIBUTE,
    Role.MANAGE_AGENT,
    Role.MANAGE_AGENT_GROUP,
    Role.REFERENTIAL_MANAGE,
  ],
  DELIVERY: [Role.MERCHANT, Role.PARCELS, Role.MANAGE_DELIVERYMAN],
  MESSAGING: [
    Role.MANAGE_FLOW,
    Role.BROADCAST_SCHEDULE,
    Role.AUTOMATION_DEF_ACTIONS,
    Role.BROADCAST,
    Role.BROADCAST_ADD,
    Role.BROADCAST_VIEW,
    Role.MANAGE_ATTRIBUTE,
    Role.MANAGE_BOTS,
    Role.MANAGE_AIASSISTANT,
    Role.WORKFLOW_VIEW,
    Role.TEAM_INBOX,
    Role.CONVERSATION_VIEW,
    Role.CONTACT_VIEW,
    Role.CONSULT_NOTIFS,
    Role.REFERENTIAL_VAR_MANAGE,
  ],
  ORDER: [Role.PRODUCT, Role.PRODUCT_VIEW, Role.ORDERS, Role.ORDERS_VIEW],
  PAYMENT: [
    Role.DASHY_COMPANIES,
    MANAGE_PAYMENT_CONFIG,
    Role.MANAGE_PAYMENT_LINK,
    Role.MANAGE_SAVED_CARDS,
    Role.MANAGE_PAYMENT_AUTHZ,
    Role.PAYMENT_SIM_ACCESS,
    Role.PAYMENT_VIEW,
    Role.MANAGE_PAYMENT_ONBOARDING,
    Role.PAYMENT_ONBOARDING_VIEW,
  ],
  SOLUTION: [],
  SUPPORT: [Role.MANAGE_TICKET, Role.TICKET_VIEW],
  VOUCHER: [Role.MANAGE_VOUCHER, Role.VOUCHER_VIEW],
};

export interface PermissionGroup {
  group: PermissionGroupName;
  permissions: string[];
  /**
   * True when the group hands out no permission of its own, so belonging to it is the audience:
   * the picker targets `groupPath(group)` instead of ticking permissions.
   */
  groupOnly: boolean;
}

/** Every group in the canonical order, each with the permissions it grants. */
export function buildPermissionGroups(): PermissionGroup[] {
  return PERMISSION_GROUP_NAMES.map((group) => ({
    group,
    permissions: [...PERMISSIONS_BY_GROUP[group]],
    groupOnly: PERMISSIONS_BY_GROUP[group].length === 0,
  }));
}
